import { useEffect, useState, useCallback } from "react";
import { MessageSquare } from "lucide-react";
import MessageComposer from "./MessageComposer";
import { listBookingCommunications, COMMUNICATION_TYPE_META, OUTCOME_META, CHANNEL_META } from "../../lib/communications";

// The set of types staff can manually prepare/send from a booking. Booking
// confirmation/reconfirmation already have dedicated flows elsewhere on
// this page; review invitations send automatically on completion: neither
// belongs in this generic composer list.
const SENDABLE_TYPES = [
  "confirmation_reminder", "pickup_reminder", "late_return", "deposit_reminder",
  "deposit_refund_update", "vehicle_replacement", "booking_change_response", "cancellation",
];

export default function CommunicationsPanel({ bookingId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await listBookingCommunications(bookingId);
      setItems(data);
    } catch {
      // no permission: stay hidden below
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="communications-panel">
      <h2 className="font-display text-base font-semibold inline-flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-[#1B4332]" /> Communications
      </h2>

      <div className="flex flex-wrap gap-2">
        {SENDABLE_TYPES.map((t) => (
          <MessageComposer key={t} bookingId={bookingId} communicationType={t}
                            onSent={() => { setActiveType(null); load(); }} />
        ))}
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">History</h3>
        {loading ? (
          <div className="text-sm text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-400 italic">Nothing sent for this booking yet.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="communications-history-list">
            {items.map((it) => {
              const typeLabel = (COMMUNICATION_TYPE_META[it.communication_type] || {}).label || it.communication_type;
              const outcome = OUTCOME_META[it.outcome] || { label: it.outcome, bg: "bg-slate-100", text: "text-slate-700" };
              const channelLabel = it.channel ? (CHANNEL_META[it.channel] || {}).label || it.channel : "";
              return (
                <li key={it.id} className="flex items-center justify-between text-sm rounded-md border border-slate-100 px-3 py-2">
                  <div>
                    <span className="font-medium">{typeLabel}</span>
                    {channelLabel && <span className="text-slate-400"> · {channelLabel}</span>}
                    {it.error_message && <div className="text-xs text-rose-600 mt-0.5">{it.error_message}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-400">{it.created_at ? new Date(it.created_at).toLocaleString() : ""}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${outcome.bg} ${outcome.text}`}>
                      {outcome.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
